import React, { useEffect } from 'react';
import { useSideBar } from './SideBarContext';
import { useAtom } from 'jotai';
import { darkModeAtom } from '../../Services/store/atoms';
import useMediaQuery from '../../hooks/useMediaQuery';
import { HiX } from 'react-icons/hi';

const SideBar = ({ children, header, mobileHeader, footer, className = '' }) => {
  const { open, openMobile, setOpenMobile } = useSideBar();
  const [darkMode] = useAtom(darkModeAtom);
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  // Bloquea el scroll de la página mientras el menú está abierto en el celular.
  // Antes se fijaba el <body> (position: fixed + top negativo): en Safari de
  // iPhone eso cambiaba el tamaño de la barra de direcciones y el header daba
  // un salto al abrir, y al cerrar la página volvía arriba un instante antes de
  // restaurar el scroll. Con overflow: hidden solo en <html> la página no se
  // mueve; el overlay ya tiene touch-action: none y la lista del menú
  // overscroll-behavior: contain para que su scroll no pase a la página.
  // No se toca el <body>: con overflow: hidden ahí, el body pasa a ser el
  // contenedor de scroll y el header sticky se pega a él y salta.
  useEffect(() => {
    if (!isDesktop && openMobile) {
      const html = document.documentElement;
      const prev = html.style.overflow;
      html.style.overflow = 'hidden';
      return () => { html.style.overflow = prev; };
    }
  }, [isDesktop, openMobile]);

  return (
    <>
      {/* Mobile overlay */}
      {!isDesktop && openMobile && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm"
          style={{ zIndex: 10001, overscrollBehavior: 'contain', touchAction: 'none' }}
          onClick={() => setOpenMobile(false)}
          onTouchMove={(e) => e.preventDefault()}
        />
      )}

      {/* Mobile Drawer */}
      {!isDesktop && (
        <aside
          className={`ap-app fixed top-0 left-0 h-full bg-white dark:bg-gray-900 transition-all duration-300 ease-out ${openMobile ? 'translate-x-0' : '-translate-x-full'}`}
          style={{ zIndex: 10002, width: 'min(320px, 85vw)' }}
        >
          <div className="flex flex-col h-full relative">
            <div className="shrink-0 flex items-start justify-between px-4 py-4">
              <div className="flex-1">
                {mobileHeader}
              </div>
              <button
                onClick={() => setOpenMobile(false)}
                aria-label="Cerrar menú"
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors ml-2"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-2">
              {children}
            </div>
            {footer && (
              <ul className="list-none shrink-0 border-t border-gray-200 dark:border-gray-700/70 bg-gray-50/95 dark:bg-gray-950/60 backdrop-blur-sm p-3 space-y-1.5">
                {footer}
              </ul>
            )}
          </div>
        </aside>
      )}

      {/* Desktop Sidebar */}
      {isDesktop && (
        <aside style={{ zIndex: 10002 }} className={`ap-app flex flex-col fixed top-0 left-0 h-full bg-white dark:bg-gray-900 transition-all duration-300 ease-out ${open ? 'w-[17rem]' : 'w-[4.5rem]'} ${className}`}>
          {header && (
            <div className={`shrink-0 ${open ? 'px-4 py-3' : 'flex justify-center items-center py-3'}`}>
              {header}
            </div>
          )}
          <nav className={`flex-1 min-h-0 overflow-y-auto ${open ? 'px-2 py-4' : 'flex flex-col items-center px-1 py-4'}`}>
            {children}
          </nav>
          {footer && (
            <ul className={`list-none shrink-0 border-t border-gray-200 dark:border-gray-700/70 bg-gray-50/80 dark:bg-gray-950/50 py-3 space-y-1.5 ${open ? 'px-2' : 'flex flex-col items-center px-1'}`}>
              {footer}
            </ul>
          )}
        </aside>
      )}
    </>
  );
};

export default SideBar;
